import React, { useState } from 'react';

const KuciuStalasGame = ({ gameData, onAddScore, onComplete, playScoreSound }) => {
    const [placedDishes, setPlacedDishes] = useState([]);
    const [selectedDish, setSelectedDish] = useState(null);
    const [showTradition, setShowTradition] = useState(null);

    const dishes = gameData?.dishes || [];
    const tableItems = gameData?.table_items || [];
    const rules = gameData?.rules || [];

    const handleDishSelect = (dish) => {
        if (showTradition) return;
        if (navigator.vibrate) navigator.vibrate(30);
        setSelectedDish(selectedDish?.id === dish.id ? null : dish);
    };

    const handleTableClick = () => {
        if (selectedDish && !placedDishes.find(d => d.id === selectedDish.id)) {
            const newPlaced = [...placedDishes, selectedDish];
            setPlacedDishes(newPlaced);
            if (navigator.vibrate) navigator.vibrate(60);
            
            // Assuming score handling via onAddScore and final callback
            onAddScore?.(gameData.points_per_dish || 10);

            if (newPlaced.length >= dishes.length) {
                if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
                setTimeout(() => {
                    onComplete?.();
                }, 3000);
            }
            setSelectedDish(null);
        }
    };

    const remainingDishes = dishes.filter(d => !placedDishes.find(p => p.id === d.id));

    if (!gameData) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-red-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🕯️</div>
                <h3 className="text-base md:text-3xl font-black text-red-900 tracking-tighter uppercase">Ruošiamas šventinis stalas...</h3>
            </div>
        );
    }

    const progress = (placedDishes.length / dishes.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Cultural Context Area */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-red-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col lg:flex-row gap-3 md:p-8 relative z-10">
                    <div className="flex-1">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
                                🕯️
                            </div>
                            <div>
                                <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                                    {gameData.title || 'Kūčių Stalas'}
                                </h3>
                                <p className="text-slate-500 font-bold italic font-serif">
                                    {gameData.instructions || 'Sudėliok tradicinį stalą pagal protėvių papročius!'}
                                </p>
                            </div>
                        </div>

                        {/* Rules HUD */}
                        <div className="bg-white/60 backdrop-blur-md rounded-[2rem] p-2 md:p-6 border-2 border-white/80 shadow-inner">
                            <h4 className="text-[10px] font-black text-red-400 uppercase tracking-[0.3em] mb-4">Senosios Tradicijos</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {rules.map((rule, idx) => (
                                    <div key={idx} className="flex items-start gap-3 text-slate-600 font-bold text-sm bg-white/40 p-3 rounded-2xl border border-white/20">
                                        <span className="text-red-500 text-lg leading-none mt-0.5">•</span>
                                        <p className="leading-tight">{rule}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Table Elements Summary */}
                    <div className="lg:w-[350px] space-y-4">
                         <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] px-4">Stalo Elementai</h4>
                         <div className="space-y-3">
                            {tableItems.slice(0, 3).map((item, idx) => (
                                <div key={idx} className="flex items-center gap-4 bg-white/60 p-4 rounded-[2rem] border-2 border-white shadow-xl">
                                    <span className="text-lg md:text-4xl drop-shadow-lg">{item.emoji}</span>
                                    <div className="overflow-hidden">
                                        <p className="text-sm font-black text-slate-800 leading-none mb-1 truncate uppercase tracking-tighter">{item.name}</p>
                                        <p className="text-[10px] text-slate-500 font-bold italic font-serif leading-tight">{item.reason}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Interactive Ritual Area */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 md:p-8 items-stretch">
                
                {/* Pantry / Dish Palette (Left) */}
                <div className="xl:col-span-4 space-y-6">
                    <div className="flex items-center justify-between px-6">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Paruošti Valgiai</h4>
                        <span className="bg-red-500 text-white px-4 py-1 rounded-full text-[10px] font-black shadow-lg">
                            {remainingDishes.length} LIKO
                        </span>
                    </div>
                    <div className="premium-glass rounded-2xl md:rounded-[4rem] p-3 md:p-8 border-white/60 shadow-2xl bg-white/40 backdrop-blur-xl max-h-[700px] overflow-y-auto custom-scrollbar">
                        <div className="grid grid-cols-2 gap-2 md:p-6">
                            {remainingDishes.map((dish) => (
                                <button
                                    key={dish.id}
                                    onClick={() => handleDishSelect(dish)}
                                    className={`
                                        premium-button-3d relative p-3 md:p-8 rounded-xl md:rounded-[3rem] transition-all duration-500 border-b-[10px] flex flex-col items-center group
                                        ${selectedDish?.id === dish.id 
                                            ? 'bg-red-500 text-white border-red-700 scale-105 shadow-red-500/20' 
                                            : 'bg-white text-slate-800 border-slate-100 hover:bg-slate-50'}
                                    `}
                                >
                                    <div className={`text-7xl mb-4 drop-shadow-2xl transition-transform duration-500 ${selectedDish?.id === dish.id ? 'animate-bounce' : 'group-hover:scale-110'}`}>
                                        {dish.emoji}
                                    </div>
                                    <div className={`text-sm font-black text-center leading-tight uppercase tracking-tighter ${selectedDish?.id === dish.id ? 'text-white' : 'text-slate-800'}`}>
                                        {dish.name}
                                    </div>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); if (navigator.vibrate) navigator.vibrate(20); setShowTradition(dish); }}
                                        className={`mt-4 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-colors ${selectedDish?.id === dish.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-400 hover:text-red-500'}`}
                                    >
                                        Istorija 💡
                                    </button>
                                </button>
                            ))}
                        </div>
                        {remainingDishes.length === 0 && (
                            <div className="py-20 text-center space-y-6">
                                <div className="text-8xl animate-float">🎊</div>
                                <p className="text-2xl font-black text-emerald-600 uppercase tracking-tighter italic font-serif">Visi valgiai paruošti!</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Holy Table (Right) */}
                <div className="xl:col-span-8 space-y-6">
                    <div className="flex items-center justify-between px-6">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Šventinis Stalas</h4>
                        <div className="w-64 bg-slate-200/50 rounded-full h-3 p-1 shadow-inner overflow-hidden border border-slate-300">
                            <div 
                                className="h-full bg-gradient-to-r from-red-400 via-green-500 to-red-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(239,68,68,0.3)]" 
                                style={{ width: `${progress}%` }} 
                            />
                        </div>
                    </div>
                    <div 
                        onClick={handleTableClick}
                        className={`
                            premium-glass rounded-3xl md:rounded-[5rem] p-4 md:p-12 lg:p-4 md:p-20 min-h-[50vh] border-white/60 shadow-2xl relative transition-all duration-700 overflow-hidden flex flex-col items-center justify-center
                            bg-slate-900 text-white
                            ${selectedDish ? 'ring-8 ring-red-500/30 cursor-crosshair scale-[0.98]' : 'cursor-default'}
                        `}
                    >
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.1)_0%,transparent_80%)] pointer-events-none" />
                        <div className="absolute top-4 md:p-12 left-1/2 -translate-x-1/2 flex items-center gap-2 md:p-6 text-red-500/40 font-black uppercase tracking-[0.6em] text-sm">
                            <span className="animate-pulse">🕯️</span> Ramybė ir Tradicijos <span className="animate-pulse">🕯️</span>
                        </div>

                        {placedDishes.length === 0 ? (
                            <div className="relative z-10 flex flex-col items-center justify-center text-center space-y-4 md:space-y-10 py-20 opacity-40">
                                <div className="text-2xl md:text-6xl md:text-[180px] drop-shadow-[0_0_80px_rgba(239,68,68,0.3)] animate-float">🍽️</div>
                                <div className="space-y-4">
                                    <p className="text-lg md:text-4xl font-black uppercase tracking-tighter">Stalas Laukia</p>
                                    <p className="text-xl font-bold italic font-serif">Pasirink patiekalą iš atsargų ir padėk jį čia</p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:p-8 relative z-10 w-full pt-16">
                                {placedDishes.map((dish) => (
                                    <div
                                        key={dish.id}
                                        className="bg-white/10 backdrop-blur-xl rounded-xl md:rounded-[3rem] p-3 md:p-8 shadow-2xl border-2 border-white/10 flex flex-col items-center animate-pop-in relative group"
                                    >
                                        <div className="absolute -top-4 -right-4 w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-xl shadow-xl border-4 border-slate-900">✓</div>
                                        <div className="text-8xl mb-4 drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)] group-hover:scale-110 transition-transform">{dish.emoji}</div>
                                        <div className="text-lg font-black text-white text-center leading-none uppercase tracking-tighter">{dish.name}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedDish && (
                            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-red-600 text-white px-12 py-5 rounded-lg md:rounded-[2.5rem] font-black text-2xl shadow-[0_20px_60px_rgba(220,38,38,0.4)] animate-bounce uppercase tracking-widest border-4 border-red-400">
                                DĖTI {selectedDish.emoji} ČIA 📍
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tradition Insight Modal */}
            {showTradition && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" onClick={() => setShowTradition(null)} />
                    <div className="max-w-2xl w-full p-4 md:p-12 lg:p-4 md:p-16 rounded-3xl md:rounded-[5rem] bg-white shadow-[0_40px_100px_rgba(0,0,0,0.5)] relative animate-pop-in border-[12px] border-red-500 text-center">
                        <div className="absolute -top-4 md:p-16 left-1/2 -translate-x-1/2 w-32 h-32 bg-white rounded-full flex items-center justify-center text-8xl shadow-2xl border-8 border-red-500 animate-float">
                            {showTradition.emoji}
                        </div>
                        
                        <div className="space-y-4 md:space-y-10 pt-10">
                            <div>
                                <h4 className="text-[10px] font-black text-red-400 uppercase tracking-[0.5em] mb-4">Patiekalo Kilmė</h4>
                                <h3 className="text-xl md:text-5xl font-black text-slate-900 tracking-tighter uppercase leading-none">{showTradition.name}</h3>
                            </div>
                            
                            <p className="text-2xl text-slate-500 font-bold leading-relaxed italic font-serif px-6">
                                "{showTradition.description}"
                            </p>
                            
                            {showTradition.tradition && (
                                <div className="bg-red-50 rounded-xl md:rounded-[3rem] p-4 md:p-10 border-2 border-red-100 text-left relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 p-3 md:p-8 text-7xl opacity-[0.03] select-none pointer-events-none group-hover:scale-125 transition-transform">📖</div>
                                    <h5 className="text-[10px] font-black text-red-500 uppercase tracking-widest mb-4">Išmintis</h5>
                                    <p className="text-2xl text-red-800 font-bold italic font-serif leading-relaxed relative z-10">
                                        "{showTradition.tradition}"
                                    </p>
                                </div>
                            )}
                            
                            <button
                                onClick={() => setShowTradition(null)}
                                className="premium-button-3d w-full py-3 md:py-8 bg-gradient-to-r from-red-500 to-green-600 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-3xl shadow-2xl shadow-green-900/20 uppercase tracking-[0.2em] active:scale-95 transition-all"
                            >
                                SUPRATAU ✓
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KuciuStalasGame;
